import React, {useRef} from "react";
import {useHistory} from "react-router-dom";
import {Col, Descriptions, Row, Skeleton, Tooltip} from "antd";
import {
    SisternodeOutlined,
    EditOutlined,
    ThunderboltFilled,
    InsertRowAboveOutlined,
    ScheduleOutlined,
    AlertFilled,
    StarFilled,
    DollarOutlined,
    BranchesOutlined
} from "@ant-design/icons";

import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {CourierIcon} from "../../_defaultComponents/CourierIcon/CourierIcon";
import {useTranslation} from "react-i18next";
import {H3Map} from "../../H3Map/H3Map";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";

const PoolDetail = ({poolId}) => {
    const {t} = useTranslation(["pools"]);
    const history = useHistory();
    const stores = useStore();
    const mapRef = useRef(null);

    const [loading, pool] = useFetch({
        fetchFn: stores.poolStore.getPool.bind(null, {poolId}),
        initialCall: true
    });

    const [isCityH3CellsLoading, cityH3Cells] = useFetch({
        fetchFn: stores.poolStore.getPoolH3Cells.bind(null, {city: pool?.city}),
        dependencies: [pool?.city],
        initialCall: !!pool?.city
    });

    const otherHexagons = React.useMemo(() => {
        if (!cityH3Cells?.data || !Array.isArray(cityH3Cells.data)) return [];

        const hexagons = cityH3Cells.data.flatMap(pool => pool.h3Cells || []);

        return hexagons;
    }, [cityH3Cells]);

    return (
        <div data-testid="userDetailScreen">
            {loading ? (
                <Skeleton paragraph={{rows: 2, width: "100%"}}></Skeleton>
            ) : (
                <Row gutter={[0, 20]}>
                    <Col span={24}>
                        <Descriptions
                            title={
                                <Row type="flex" justify="space-between">
                                    <div>{pool?.name}</div>

                                    <div>
                                        <Tooltip title={t("tooltips.editCouriers", {ns: "pools"})}>
                                            <CustomButton
                                                type="primary"
                                                data-testid="editPoolCouriersButton"
                                                href={`/pools/${poolId}/couriers`}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    history.push(`/pools/${poolId}/couriers`);
                                                }}
                                                icon={<CourierIcon />}
                                            />
                                        </Tooltip>
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={"Havuz Yetkililerini Düzenle"}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="editPoolUsersButton"
                                                    href={`/pools/${poolId}/authorized-users`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(
                                                            `/pools/${poolId}/authorized-users`
                                                        );
                                                    }}
                                                    icon={<AlertFilled />}
                                                />
                                            </Tooltip>
                                        )}
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={"Havuz Fiyatlandırması"}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="listPaymentContractsButton"
                                                    href={`/pools/${poolId}/payment-contracts`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(
                                                            `/pools/${poolId}/payment-contracts`
                                                        );
                                                    }}
                                                    icon={<DollarOutlined />}
                                                />
                                            </Tooltip>
                                        )}
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={"Havuz Birleştirme Ayarları"}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="listMergeOptionsButton"
                                                    href={`/pools/${poolId}/merge-options`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(
                                                            `/pools/${poolId}/merge-options`
                                                        );
                                                    }}
                                                    icon={<BranchesOutlined />}
                                                />
                                            </Tooltip>
                                        )}
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={"Destek Havuz Ayarları"}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="listSupportPoolSettingsButton"
                                                    href={`/pools/${poolId}/support-pool-settings`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(
                                                            `/pools/${poolId}/support-pool-settings`
                                                        );
                                                    }}
                                                    icon={<SisternodeOutlined />}
                                                />
                                            </Tooltip>
                                        )}
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={t("tooltips.edit", {ns: "pools"})}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="editPoolButton"
                                                    href={`/pools/${poolId}/edit`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(`/pools/${poolId}/edit`);
                                                    }}
                                                    icon={<EditOutlined />}
                                                />
                                            </Tooltip>
                                        )}
                                        {!stores.meStore.checkRole("isSOperationUser") && (
                                            <Tooltip title={"Forecasts"}>
                                                <CustomButton
                                                    type="primary"
                                                    data-testid="forecasts"
                                                    href={`/pools/${poolId}/daily-forecasts`}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        history.push(
                                                            `/pools/${poolId}/daily-forecasts`
                                                        );
                                                    }}
                                                    icon={<ThunderboltFilled />}
                                                />
                                            </Tooltip>
                                        )}
                                        <Tooltip title={"Slots"}>
                                            <CustomButton
                                                type="primary"
                                                data-testid="slots"
                                                href={`/pools/${poolId}/daily-slots`}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    history.push(`/pools/${poolId}/daily-slots`);
                                                }}
                                                icon={<InsertRowAboveOutlined />}
                                            />
                                        </Tooltip>
                                        <Tooltip title={"Vardiyalar"}>
                                            <CustomButton
                                                type="primary"
                                                data-testid="slots"
                                                href={`/pools/courier-shifts?poolId=${poolId}`}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    history.push(
                                                        `/pools/courier-shifts?poolId=${poolId}`
                                                    );
                                                }}
                                                icon={<ScheduleOutlined />}
                                            />
                                        </Tooltip>
                                        <Tooltip title={"Kurye Bonus Kampanyaları"}>
                                            <CustomButton
                                                type="primary"
                                                data-testid="courier-bonus-campaigns"
                                                href={`/pools/${poolId}/courier-bonus-campaigns`}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    history.push(
                                                        `/pools/${poolId}/courier-bonus-campaigns`
                                                    );
                                                }}
                                                icon={<StarFilled />}
                                            />
                                        </Tooltip>
                                    </div>
                                </Row>
                            }
                            column={1}
                            bordered
                        >
                            <Descriptions.Item label={t("fields.poolRegion", {ns: "pools"})}>
                                <span data-testid="name">{pool?.name || "-"}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.city", {ns: "common"})}>
                                <span data-testid="city">{pool?.city?.capitalize() || "-"}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.company", {ns: "common"})}>
                                <span data-testid="companies">
                                    {pool?.companies?.length > 0
                                        ? pool.companies
                                              .map(company => company.companyName)
                                              .join(", ")
                                        : "-"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.subcompany", {ns: "common"})}>
                                <span data-testid="subcompanies">
                                    {pool?.subCompanies?.length > 0
                                        ? pool.subCompanies.map(company => company.name).join(", ")
                                        : "-"}
                                </span>
                            </Descriptions.Item>

                            <Descriptions.Item label={t("fields.status", {ns: "common"})}>
                                <span data-tesid="isActive">
                                    {pool?.isActive
                                        ? t("other.active", {ns: "common"})
                                        : t("other.inactive", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("filters.integrationType", {ns: "common"})}>
                                <span data-tesid="integrationType">
                                    {pool?.integrationType === "pool"
                                        ? "Havuz"
                                        : pool?.integrationType === "dedicated"
                                        ? "Dedike"
                                        : "Dedike Entegrasyon"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.testPool", {ns: "common"})}>
                                <span data-testid="testPool">
                                    {pool?.isTestPool
                                        ? t("other.yes", {ns: "common"})
                                        : t("other.no", {ns: "common"})}
                                </span>
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                    <Col span={24}>
                        <H3Map
                            ref={mapRef}
                            markers={
                                pool?.subCompanies?.map(subCompany => ({
                                    _id: subCompany._id,
                                    name: subCompany.name,
                                    location: {
                                        latitude: subCompany.location?.latitude,
                                        longitude: subCompany.location?.longitude
                                    }
                                })) || []
                            }
                            defaultCenter={{
                                lat: pool?.subCompanies?.[0]?.location?.latitude || 41.0,
                                lng: pool?.subCompanies?.[0]?.location?.longitude || 29.0
                            }}
                            initialHexagons={pool?.h3Cells || []}
                            showToolsPanel={false}
                            showOtherHexagonsPanel={true}
                            isActionsEnabled={false}
                            isPreview={true}
                            otherHexagons={otherHexagons}
                        />
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default PoolDetail;
